import { useBoolean } from '@lfg/core/hooks';
import { convertToTiers } from '@lfg/core/lib';
import { useCallback, useState } from 'react';

import { subgraphClientV1 } from '@/agent/apiClient/api';
import { USD_VALUE_DECIMAL } from '@/global/configs/decimal.config';
import type { ProjectSGSchemaType } from '@/global/schemas/subgraph.schema';
import { Nullable } from '@/global/types/common.type';
import type { ProjectSG } from '@/global/types/project.type';
import type { ProjectV1Hook } from '@/hooks/types/project.type';
import { setProject as setProjectRedux } from '@/store/actions/project.action';
import { useAppDispatch } from '@/store/utils';

import { calculateCurrentTier, calculateCurrentTierIndex, formatProjectData } from '../utils/helpers';

async function getProjectByIdSGV1(id: string): Promise<Nullable<ProjectSGSchemaType>> {
  const query = `
    query GetProject($id: ID!) {
      project(id: $id) {
        admin
        blockNumber
        cap
        blockTimestamp
        description
        fee
        id
        name
        nft
        projectImage
        sold
        startSaleTimestamp
        termsOfServiceLink
        transactionHash
        uri
        website
      }
      projectTierPrice(id: $id) {
        blockNumber
        blockTimestamp
        border
        price
        id
      }
    }
  `;

  try {
    const { data } = await subgraphClientV1({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        query,
        variables: { id },
      },
    });

    return data.data;
  } catch (error) {
    return null;
  }
}

const useProjectV1 = (): ProjectV1Hook => {
  const { value: loading, setTrue: setLoadingTrue, setFalse: setLoadingFalse } = useBoolean(false);
  const dispatch = useAppDispatch();
  const [error, setError] = useState<Error | undefined>(undefined);
  const [projectInfo, setProjectInfo] = useState<ProjectSG | null>(null);

  const getProjectInfo = useCallback(
    async (id: string, projectData?: ProjectSGSchemaType) => {
      if (!id) return null;
      setLoadingTrue();
      setError(undefined);

      try {
        const projectRes = projectData ?? (await getProjectByIdSGV1(id));
        if (!projectRes) throw new Error('Failed to fetch project info');

        const formatProject = formatProjectData(projectRes.project, projectRes.projectTierPrice);
        const totalCap = formatProject.border[formatProject.border.length - 1] ?? 0;
        const currentTierIndex = calculateCurrentTierIndex(totalCap, formatProject.sold, formatProject.border);
        const currentTier = calculateCurrentTier(
          formatProject.border,
          formatProject.price,
          formatProject.sold,
          currentTierIndex,
        );

        const project: ProjectSG = {
          ...formatProject,
          totalCap,
          currentTierIndex,
          nodeAvailability: totalCap - formatProject.sold,
          tiers: convertToTiers(
            formatProject.border,
            formatProject.price.map((price: number) => Number(price) / 10 ** USD_VALUE_DECIMAL),
          ),
          currentTier,
          nftImage: '',
          nftImageHash: '',
        };
        setProjectInfo(project);
        dispatch(setProjectRedux(project));
        return project;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch project info');
        setError(error);
        dispatch(setProjectRedux(null));
        return null;
      } finally {
        setLoadingFalse();
      }
    },
    [dispatch, setLoadingTrue, setLoadingFalse],
  );

  return {
    loading,
    error,
    projectInfo,
    getProjectInfo,
  };
};

export { useProjectV1 };
